import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, CardHeader, Col, Input, Row } from 'reactstrap'
import { IBoothRecord } from './index'
import { toast } from 'react-toastify'
import DeleteModal from 'Components/Common/DeleteModal'
import BoothNotesModal from './BoothNotesModal'
import { useDebouncedCallback } from 'use-debounce'
import { Spinner } from 'reactstrap'
import { ISort } from 'lib/type'
import { useEffect } from 'react'

interface IProds {
  actionOptions: any[]
  boothRecords: IBoothRecord[]
  handleOpenDetails: (arg: string) => void
  setOpenBoothPopup: (arg: boolean) => void
  setEditMode: (arg: boolean) => void
  handleDeleteBooth: (arg: string) => Promise<string>
  handleUpdateBoothNotes: (id: string, notes: string) => Promise<string>
  searchBooth: (arg: string) => void
  fetching: boolean
  sortAction: ISort
  setSortAction: (arg: ISort) => void
  setCanEdit: (arg: boolean) => void
}

const BoothsTable: React.FC<IProds> = ({
  boothRecords,
  handleOpenDetails,
  setOpenBoothPopup,
  setEditMode,
  actionOptions,
  handleDeleteBooth,
  handleUpdateBoothNotes,
  searchBooth,
  fetching,
  sortAction,
  setSortAction,
  setCanEdit
}) => {
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false)
  const [boothSelectedId, setBoothSelectedId] = useState<string | undefined>(undefined)
  const [query, setQuery] = useState('')

  // Booth notları modalı state
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false)
  const [notesBooth, setNotesBooth] = useState<{ id?: string; name?: string; notes: string }>({
    notes: ''
  })
  const [permissions, setPermissions] = useState({
    canCreate: true,
    canEdit: true,
    canDelete: true
  })

  // "ID'yi Unut": admin panelde booth _id'sini maskelemek için toggle set.
  // sessionStorage'da persisted — tarayıcı kapanınca sıfırlanır, kalıcı maskelemeden kaçınmak için.
  const FORGOTTEN_IDS_KEY = 'forgottenBoothIds'
  const [forgottenIds, setForgottenIds] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set()
    try {
      const raw = sessionStorage.getItem(FORGOTTEN_IDS_KEY)
      return new Set<string>(raw ? JSON.parse(raw) : [])
    } catch {
      return new Set()
    }
  })

  const persistForgottenIds = (next: Set<string>) => {
    if (typeof window === 'undefined') return
    try {
      sessionStorage.setItem(FORGOTTEN_IDS_KEY, JSON.stringify(Array.from(next)))
    } catch {
      /* storage dolu vs. — sessiz geç */
    }
  }

  const toggleForgetId = (id: string) => {
    setForgottenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      persistForgottenIds(next)
      return next
    })
  }

  const maskBoothId = (id: string) => {
    if (!id || id.length < 6) return '••••'
    return `••••••••••••••••••${id.slice(-4)}`
  }

  const handleAddBooth = () => {
    setEditMode(false)
    setOpenBoothPopup(true)
  }

  const handleEditBooth = (boothId: string | undefined) => {
    const id = boothId as string
    setEditMode(true)
    setOpenBoothPopup(true)
    handleOpenDetails(id)
  }

  const handleDeleteBoothRecord = async (boothId: string | undefined) => {
    const id = boothId as string
    const res = handleDeleteBooth(id)

    const message = await res
    if (message === 'SUCCESSFULL') {
      toast.success('Deleted a booth successfully')
    }
  }

  const handleShowModalDelete = (boothId: string | undefined) => {
    const id = boothId as string
    setShowModalDelete(true)
    setBoothSelectedId(id)
  }

  const handleOpenNotes = (boothRecord: IBoothRecord) => {
    setNotesBooth({
      id: boothRecord._id,
      name: boothRecord.name,
      notes: boothRecord.notes || ''
    })
    setShowNotesModal(true)
  }

  const handleDelete = () => {
    handleDeleteBoothRecord(boothSelectedId)
    setShowModalDelete(false)
  }

  const showPaymentMethods = (boothRecord: any) => {
    let methods: string[] = []
    const pm = boothRecord.paymentMethods
    if (pm) {
      if (pm.cash) methods.push('Cash')
      if (pm.creditCard) methods.push('Credit Card')
    } else {
      if (boothRecord.isCash) methods.push('Cash')
      if (boothRecord.isCreditCard) methods.push('Credit Card')
    }
    return methods.join(', ')
  }

  const showActiveActions = (boothRecord: IBoothRecord) => {
    if (boothRecord.activeActions && Array.isArray(boothRecord.activeActions)) {
      return boothRecord.activeActions.map((action: any) => action.label || action.name).join(', ')
    }
    return ''
  }

  const renderHardwareIndicator = (
    connected: boolean | undefined,
    label: string,
    tooltip?: string | null
  ) => {
    if (connected === undefined || connected === null) {
      return (
        <span
          title={tooltip || 'Veri yok'}
          style={{
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: '4px',
            backgroundColor: '#e9ecef',
            color: '#6c757d',
            fontWeight: '600',
            fontSize: '11px',
            border: '1px solid #ced4da'
          }}
        >
          {label}: N/A
        </span>
      )
    }
    const color = connected ? '#28a745' : '#dc3545'
    const bg = connected ? '#d4edda' : '#f8d7da'
    const text = connected ? 'Aktif' : 'Kapalı'
    return (
      <span
        title={tooltip || ''}
        style={{
          display: 'inline-block',
          padding: '3px 10px',
          borderRadius: '4px',
          backgroundColor: bg,
          color: color,
          fontWeight: '600',
          fontSize: '11px',
          border: `1px solid ${color}`
        }}
      >
        {text}
      </span>
    )
  }

  // Heartbeat interval is 1 minute, so 3 missed heartbeats = 3 minutes
  const HEARTBEAT_TIMEOUT_MS = 3 * 60 * 1000 // 3 minutes

  const renderConnectionStatus = (
    status: number | undefined,
    lastHeartbeat: string | undefined
  ) => {
    const statusConfig: Record<number, { color: string; bg: string; text: string; icon: string }> =
      {
        0: {
          color: '#dc3545',
          bg: '#f8d7da',
          text: 'Offline',
          icon: 'mdi-close-circle'
        },
        1: {
          color: '#28a745',
          bg: '#d4edda',
          text: 'Online',
          icon: 'mdi-check-circle'
        },
        2: {
          color: '#ff9800',
          bg: '#fff3e0',
          text: 'Busy',
          icon: 'mdi-clock-outline'
        }
      }

    // Check if 3 missed heartbeats (3+ minutes since last heartbeat)
    let effectiveStatus = status ?? 0
    if (lastHeartbeat && (status === 1 || status === 2)) {
      const lastHeartbeatDate = new Date(lastHeartbeat)
      const now = new Date()
      const diffMs = now.getTime() - lastHeartbeatDate.getTime()

      // If more than 3 minutes have passed, consider the booth offline
      if (diffMs > HEARTBEAT_TIMEOUT_MS) {
        effectiveStatus = 0 // OFFLINE
      }
    }

    const config = statusConfig[effectiveStatus] || statusConfig[0]

    return (
      <span
        style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: '4px',
          backgroundColor: config.bg,
          color: config.color,
          fontWeight: '600',
          fontSize: '12px',
          border: `1px solid ${config.color}`
        }}
      >
        <i className={`mdi ${config.icon}`} style={{ marginRight: '5px' }}></i>
        {config.text}
      </span>
    )
  }

  const formatLastOnline = (
    lastOnline: string | undefined,
    connectionStatus: number | undefined
  ) => {
    if (connectionStatus === 1) {
      return (
        <span
          style={{
            color: '#28a745',
            fontWeight: '600',
            fontSize: '12px'
          }}
        >
          Online Now
        </span>
      )
    }

    if (!lastOnline) {
      return (
        <span
          style={{
            color: '#6c757d',
            fontSize: '12px'
          }}
        >
          Never
        </span>
      )
    }

    const lastOnlineDate = new Date(lastOnline)
    const now = new Date()

    // Calculate difference in milliseconds using UTC time
    const diffMs = now.getTime() - lastOnlineDate.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    let timeText = ''
    if (diffMins < 1) {
      timeText = 'Just now'
    } else if (diffMins < 60) {
      timeText = `${diffMins}m ago`
    } else if (diffHours < 24) {
      timeText = `${diffHours}h ago`
    } else if (diffDays < 7) {
      timeText = `${diffDays}d ago`
    } else {
      timeText = lastOnlineDate.toLocaleDateString()
    }

    return (
      <span
        style={{
          color: '#6c757d',
          fontSize: '12px'
        }}
      >
        {timeText}
      </span>
    )
  }

  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    searchBooth(searchTerm)
  }, 1500)

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  const handleActionSort = (sortBy: string) => {
    if (sortAction.sortBy === sortBy) {
      setSortAction({
        ...sortAction,
        sortDirection: sortAction.sortDirection === 'DESC' ? 'ASC' : 'DESC'
      })
    } else {
      setSortAction({
        sortDirection: 'DESC',
        sortBy: sortBy
      })
    }
  }

  useEffect(() => {
    const userType = sessionStorage.getItem('userType')

    // Type 1 = Admin - full access to everything
    if (userType === '1') {
      setPermissions({
        canCreate: true,
        canEdit: true,
        canDelete: true
      })
      setCanEdit(true)
      return
    }

    // Type 2 = Regular user - use role-based permissions
    const userRoles = sessionStorage.getItem('userRoles')
      ? JSON.parse(atob(sessionStorage.getItem('userRoles')!))
      : null
    if (userRoles) {
      userRoles.forEach((role: any) => {
        if (role.page === 'Booths') {
          setPermissions({
            canCreate: role.canCreate,
            canEdit: role.canEdit,
            canDelete: role.canDelete
          })
          setCanEdit(role.canEdit)
        }
      })
    }
  }, [setCanEdit])

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className='align-items-center d-flex justify-content-between'>
              <div className='search-box d-inline-block col-8 d-flex align-items-center'>
                <input
                  id='search-bar-booth'
                  className='form-control search'
                  placeholder='Search booths'
                  onChange={(e) => handleInputChange(e)}
                  value={query}
                />
                <i className='bx bx-search-alt search-icon'></i>
              </div>
              {permissions.canCreate && (
                <Button className='p-2' color='primary' onClick={(e) => handleAddBooth()}>
                  <div className='flex align-items-center'>
                    {fetching ? (
                      <Spinner size='sm'> Loading... </Spinner>
                    ) : (
                      <>
                        <i className='mdi mdi-plus'></i>
                        <span className='fw-semibold ms-1'>Add Booth</span>
                      </>
                    )}
                  </div>
                </Button>
              )}
            </CardHeader>
            <CardBody>
              <div className='table-responsive table-card' style={{ overflowX: 'auto' }}>
                <table
                  className='table align-middle table-nowrap table-striped-columns mb-0'
                  style={{ minWidth: '1600px' }}
                >
                  <thead className='table-light'>
                    <tr>
                      <th
                        scope='col'
                        onClick={() => handleActionSort('_id')}
                        style={{ minWidth: '70px', cursor: 'pointer' }}
                      >
                        <div className='w-100 d-flex justify-content-between'>
                          <span>ID</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === '_id' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== '_id' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th
                        scope='col'
                        onClick={() => handleActionSort('name')}
                        style={{ minWidth: '120px', cursor: 'pointer' }}
                      >
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Name</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'name' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'name' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' style={{ minWidth: '130px' }}>
                        Status
                      </th>
                      <th scope='col' style={{ minWidth: '110px' }}>
                        Last Online
                      </th>
                      <th scope='col' style={{ minWidth: '120px' }}>
                        Location
                      </th>
                      <th scope='col' style={{ minWidth: '150px' }}>
                        Active Actions
                      </th>
                      <th scope='col' style={{ minWidth: '150px' }}>
                        Payment Methods
                      </th>
                      <th scope='col' style={{ minWidth: '100px' }}>
                        Currency
                      </th>
                      <th scope='col' style={{ minWidth: '100px' }}>
                        Language
                      </th>
                      <th
                        scope='col'
                        style={{ minWidth: '120px' }}
                      >
                        <span>IP Address</span>
                      </th>
                      <th scope='col' style={{ minWidth: '80px', textAlign: 'center' }}>
                        Printer
                      </th>
                      <th scope='col' style={{ minWidth: '100px', textAlign: 'center' }}>
                        Printer HW
                      </th>
                      <th scope='col' style={{ minWidth: '100px', textAlign: 'center' }}>
                        Camera HW
                      </th>
                      <th scope='col' style={{ minWidth: '100px', textAlign: 'center' }}>
                        POS HW
                      </th>
                      <th scope='col' style={{ minWidth: '80px', textAlign: 'center' }}>
                        Active
                      </th>
                      <th
                        scope='col'
                        style={{
                          width: '230px',
                          minWidth: '230px',
                          position: 'sticky',
                          right: 0,
                          backgroundColor: '#f3f6f9',
                          zIndex: 1
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {boothRecords.map((boothRecord, index) => {
                      return (
                        <tr key={index}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Link
                                to='#'
                                className='fw-medium'
                                style={{
                                  fontFamily: forgottenIds.has(boothRecord._id as string)
                                    ? 'inherit'
                                    : 'monospace',
                                  letterSpacing: forgottenIds.has(boothRecord._id as string) ? '2px' : 'normal'
                                }}
                                title={forgottenIds.has(boothRecord._id as string) ? 'ID gizli' : boothRecord._id}
                              >
                                {forgottenIds.has(boothRecord._id as string)
                                  ? maskBoothId(boothRecord._id as string)
                                  : boothRecord._id}
                              </Link>
                              <button
                                type='button'
                                className='btn btn-sm btn-link p-0'
                                style={{
                                  fontSize: '11px',
                                  textDecoration: 'none',
                                  color: forgottenIds.has(boothRecord._id as string) ? '#0d6efd' : '#6c757d',
                                  whiteSpace: 'nowrap'
                                }}
                                onClick={() => toggleForgetId(boothRecord._id as string)}
                                title={forgottenIds.has(boothRecord._id as string) ? 'ID göster' : 'ID gizle'}
                              >
                                {forgottenIds.has(boothRecord._id as string) ? 'Göster' : "ID'yi Unut"}
                              </button>
                            </div>
                          </td>
                          <td>{boothRecord.name}</td>
                          <td>
                            {renderConnectionStatus(
                              boothRecord.connectionStatus?.status,
                              boothRecord.connectionStatus?.lastHeartbeat
                            )}
                          </td>
                          <td>
                            {formatLastOnline(
                              boothRecord.connectionStatus?.lastHeartbeat,
                              boothRecord.connectionStatus?.status
                            )}
                          </td>
                          <td>{boothRecord.location?.name}</td>
                          <td>{showActiveActions(boothRecord)}</td>
                          <td>{showPaymentMethods(boothRecord)}</td>
                          <td>{typeof boothRecord.currency === 'string' ? boothRecord.currency : boothRecord.currency?.name || '-'}</td>
                          <td>{typeof boothRecord.language === 'string' ? boothRecord.language : boothRecord.language?.name || '-'}</td>
                          <td>{boothRecord.connectionStatus?.ip || '-'}</td>
                          <td style={{ textAlign: 'center' }}>
                            <Input
                              checked={boothRecord.isPrinterActive || false}
                              className='form-check-input code-switcher'
                              type='checkbox'
                              disabled
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {renderHardwareIndicator(
                              boothRecord.hardwareStatus?.printer?.connected,
                              'Printer',
                              boothRecord.hardwareStatus?.printer?.error || boothRecord.hardwareStatus?.printer?.name
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {renderHardwareIndicator(
                              boothRecord.hardwareStatus?.camera?.connected,
                              'Camera',
                              boothRecord.hardwareStatus?.camera?.type
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {renderHardwareIndicator(
                              boothRecord.hardwareStatus?.pos?.connected,
                              'POS',
                              boothRecord.hardwareStatus?.pos?.error
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <Input
                              checked={boothRecord.isActive || false}
                              className='form-check-input code-switcher'
                              type='checkbox'
                              disabled
                            />
                          </td>
                          <td
                            style={{
                              position: 'sticky',
                              right: 0,
                              backgroundColor: '#fff',
                              zIndex: 1
                            }}
                          >
                            <button
                              type='button'
                              className='btn btn-sm btn-light'
                              onClick={(e) => handleEditBooth(boothRecord?._id)}
                            >
                              Details
                            </button>
                            <button
                              type='button'
                              className='btn btn-sm btn-soft-info ms-2'
                              onClick={() => handleOpenNotes(boothRecord)}
                              title="Bu booth'a ait notlar"
                            >
                              <i className='mdi mdi-note-text-outline'></i>
                              <span className='ms-1'>Not</span>
                            </button>
                            {permissions.canDelete && (
                              <button
                                type='button'
                                className='btn btn-sm btn-danger ms-2'
                                onClick={(e) => handleShowModalDelete(boothRecord?._id)}
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <DeleteModal
        show={showModalDelete}
        onDeleteClick={() => handleDelete()}
        onCloseClick={() => setShowModalDelete(false)}
      />
      <BoothNotesModal
        isOpen={showNotesModal}
        setOpenPopup={setShowNotesModal}
        boothId={notesBooth.id}
        boothName={notesBooth.name}
        currentNotes={notesBooth.notes}
        canEdit={permissions.canEdit}
        handleUpdateBoothNotes={handleUpdateBoothNotes}
      />
    </React.Fragment>
  )
}
export default BoothsTable
